import { selectPreferredPreviewTab } from "./BrowserPreviewPanel.logic";
import { deriveBrowserSidebarLabel } from "./ThreadRightPanelRouteLayout";

/** The slice of browser state a label needs — nothing about how it is stored. */
export interface BrowserSurfaceLabelInput {
  readonly browserThreadState:
    | {
        readonly session?: BrowserSessionLike | null;
        readonly selectedBrowserId?: string | null;
        readonly sessionsByBrowserId?: Readonly<Record<string, BrowserSessionLike>>;
      }
    | null
    | undefined;
  readonly previewTabId: string | null | undefined;
}

interface BrowserSessionLike {
  readonly browserId?: string;
  readonly tabs: Parameters<typeof selectPreferredPreviewTab>[0]["tabs"];
  readonly activeTabId: Parameters<typeof selectPreferredPreviewTab>[0]["activeTabId"];
}

/**
 * The label a browser surface shows for a thread.
 *
 * Extracted from the route because it is decision logic, not rendering: which
 * session is selected, which of its tabs the route is pointing at, and what to
 * call the result. Inline in a `useMemo` it counted against the component's
 * complexity and was reachable only by mounting the whole route, which is what
 * put `browserSidebarLabel` over the CRAP threshold — high branch count, no
 * direct test. As a function it is covered by the cases below it.
 */
export function resolveBrowserSurfaceLabel(input: BrowserSurfaceLabelInput): string {
  const { browserThreadState, previewTabId } = input;
  const session = browserThreadState?.session ?? null;
  const selectedBrowserId = browserThreadState?.selectedBrowserId ?? session?.browserId;
  const selected =
    (selectedBrowserId ? browserThreadState?.sessionsByBrowserId?.[selectedBrowserId] : null) ??
    session;
  const tab = selected
    ? selectPreferredPreviewTab({
        tabs: selected.tabs,
        routeTabId: previewTabId ?? null,
        activeTabId: selected.activeTabId,
      })
    : null;
  return deriveBrowserSidebarLabel({ title: tab?.title ?? null, url: tab?.url ?? null });
}
